import { SetStateAction, useEffect, useState } from 'react';
import Inspection from './pages/Inspection/Inspection';
import {
  HashRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { CssBaseline, Box, Backdrop, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { theme as defaultTheme } from './theme';
import { qcTheme } from './qcTheme';
import InspectionTop from './pages/InspectionTop/InspectionTop';
import Header from './_common/Header/Header';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from './stores/store';
import PartSelection from './pages/PartSelection/PartSelection';
import ConfirmedInspection from './pages/ConfirmedInspection/ConfirmedInspection';
import './i18n';
import { useTranslation } from 'react-i18next';
import RepairRequestSearch from './pages/RepairRequestSearch/RepairRequestSearch';
import {
  INSPEX_PATHNAME,
  SESSION_STORAGE_KEY,
  PATHNAME_STATUS_MAP,
} from './_constants/Const';
import {
  fetchRepairRequest,
  resetRepairRequest,
} from './stores/repairRequest/slice';
import ManageTemplate from './pages/ManageTemplate/ManageTemplate';
import ManageInspectionItem from './pages/ManageInspectionItem/ManageInspectionItem';
import { Theme } from '@emotion/react';
import useResetStore from './hooks/useResetStore';
import ManageUser from './pages/ManageUser/ManageUser';
import ConfirmedQc from './pages/ConfirmedQc/ConfirmedQc';
import {
  isDefaultTheme,
  isEmptyData,
  isInspectionScreen,
  isManageScreen,
  setSessionStorageRedirectUrl,
  getRedirectUrlByStatus,
} from './_utils/utils';
import Report from './pages/Report/Report';
import { createSelector } from 'reselect';
import Login from './pages/Login/Login';
import { getMe, postLogout } from './stores/userInfo/slice';
import SerialCheck from './pages/SerialCheck/SerialCheck';
import ManageOrganization from './pages/ManageOrganization/ManageOrganization';
import ExFM from './pages/_ExFM/ExFM';
import ManageCodeMaster from './pages/ManageCodeMaster/ManageCodeMaster';
import ManageHistory from './pages/ManageHistory/ManageHistory';
import { LOADING_BACKDROP_STYLE } from './_constants/styleConst';
import { isRejected } from '@reduxjs/toolkit';
import { isGetRepairRequest, isGetUserInfo } from './_utils/typeGuardUtils';
import useTempDataReflection from './hooks/useTempDataReflection';
import useErrorModal from './hooks/useErrorModal';
import ErrorModal from './_common/Modal/ErrorModal';
import { isNormalUser } from './_utils/utils';
import InputResult from './pages/InputResult/InputResult';
import { API_CODE } from './_constants/apiConst';
import ReportFormat from './pages/ManageTemplate/ReportFormat';
import ManageCriteria from './pages/ManageCriteria/ManageCriteria';

// メモ化されたセレクターを作成
const selectLoadingStatuses = createSelector(
  (state: RootState) => state,
  (state) => {
    const keys = Object.keys(state) as (keyof RootState)[];
    return keys.map((key) => state[key].status);
  },
);

const App: React.FC = () => {
  const { error, showApiError, showTypeError, hideError } = useErrorModal();
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [isLoading, setIsLoading] = useState(false);
  const loadingStatuses = useSelector(selectLoadingStatuses);

  useEffect(() => {
    if (loadingStatuses.includes('loading')) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [loadingStatuses]);

  return (
    <ThemeProvider theme={theme}>
      <HashRouter>
        <CssBaseline />
        <Header />
        <Box component="main" sx={{ flexGrow: 1, p: 0 }}>
          <Pages
            setTheme={setTheme}
            showApiError={showApiError}
            showTypeError={showTypeError}
          />
          {error && <ErrorModal message={error} onClose={hideError} />}
        </Box>
        <Backdrop
          sx={{
            ...LOADING_BACKDROP_STYLE,
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </HashRouter>
    </ThemeProvider>
  );
};

interface PagesProps {
  setTheme: React.Dispatch<SetStateAction<Theme>>;
  showApiError: (
    apiError: unknown,
    apiCode: string,
    onCloseCallback?: () => void,
  ) => void;
  showTypeError: (apiCode: string) => void;
}
const Pages: React.FC<PagesProps> = ({
  setTheme,
  showApiError,
  showTypeError,
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { resetInspectionStore, resetManageStore } = useResetStore();
  const { tempDataReflection } = useTempDataReflection();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );

  // アプリ初期化時のユーザー情報取得用のuseEffect
  useEffect(() => {
    (async () => {
      if (location.pathname === INSPEX_PATHNAME.LOGIN) {
        // ログイン画面の場合は、Loginコンポーネントで確認
        return;
      }
      // ユーザー情報取得
      const response = await dispatch(getMe());
      if (isRejected(response)) {
        setSessionStorageRedirectUrl();
        navigate(INSPEX_PATHNAME.LOGIN);
        return;
      }
      if (!isGetUserInfo(response.payload)) {
        await dispatch(postLogout());
        setSessionStorageRedirectUrl();
        showTypeError(API_CODE.AUTH);
        return;
      }
      // 言語設定の反映
      i18n.changeLanguage(response.payload.language);
    })();
  }, []);

  // 画面遷移時にスクロール位置を左上にセット
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // テーマカラー変更用のuseEffect
  useEffect(() => {
    (async () => {
      if (isDefaultTheme(location.pathname, repairRequest)) {
        setTheme(defaultTheme);
      } else {
        setTheme(qcTheme);
      }
    })();
  }, [location.pathname, repairRequest]);

  // 修理依頼と診断情報取得、削除用のuseEffect
  useEffect(() => {
    (async () => {
      if (isEmptyData(userData.userId)) {
        return;
      }

      if (isInspectionScreen(location.pathname)) {
        // 診断関連画面の場合
        // リロードした時にデータが無ければ取得する
        const urlParams = new URLSearchParams(location.search);
        const tempKey = urlParams.get('key');
        const tempRmaNo = urlParams.get('rmano');
        const isOpenByExFM = !isEmptyData(tempKey) && !isEmptyData(tempRmaNo);
        // ExFMから連携時、一時データを保存する
        if (isOpenByExFM) {
          tempDataReflection(tempKey, tempRmaNo, showApiError, showTypeError);
          return;
        }

        const rmaNo =
          tempRmaNo ?? sessionStorage.getItem(SESSION_STORAGE_KEY.RMA_NO);

        // ストレージとパラメータに修理依頼番号が無ければ一覧に戻す
        if (isEmptyData(rmaNo)) {
          navigate(INSPEX_PATHNAME.RMA_SEARCH);
          return;
        }
        // 修理依頼情報がstoreに無い場合のみ実行
        if (!repairRequest) {
          const response = await dispatch(fetchRepairRequest({ rmaNo: rmaNo }));
          // 失敗時、修理依頼一覧に戻す
          if (isRejected(response)) {
            showApiError(response.payload, API_CODE.REPAIR_REQUEST, () =>
              navigate(INSPEX_PATHNAME.RMA_SEARCH),
            );
          } else if (!isGetRepairRequest(response.payload)) {
            showTypeError(API_CODE.REPAIR_REQUEST);
          }
        }
      } else if (isManageScreen(location.pathname)) {
        // 管理系画面の場合
        if (isNormalUser(userData)) {
          // 一般権限の場合、管理系画面は表示不可
          // 画面遷移後にstoreが削除されるためここでは削除しない
          navigate(INSPEX_PATHNAME.RMA_SEARCH);
        } else {
          // storeの診断情報を削除
          dispatch(resetRepairRequest());
          resetInspectionStore();
        }
      } else if (location.pathname === INSPEX_PATHNAME.RMA_SEARCH) {
        // 修理依頼検索画面の場合
        // storeの診断情報を削除
        dispatch(resetRepairRequest());
        resetInspectionStore();
      } else {
        // ログイン画面はリダイレクト用に修理依頼情報のみ残す
        resetInspectionStore();
        resetManageStore();
      }
    })();
  }, [location.pathname, userData]);

  // 修理依頼のステータスに応じた表示制限用のuseEffect
  useEffect(() => {
    (async () => {
      if (!isInspectionScreen(location.pathname) || !repairRequest) {
        // 判定不要 || 判定不可
        return;
      }

      const { status } = repairRequest;
      // 結果入力画面を一般化したURL「/Inspection/1」→「/Inspection/」
      const generalizedPath = location.pathname.includes(
        INSPEX_PATHNAME.INSPECTION,
      )
        ? INSPEX_PATHNAME.INSPECTION
        : location.pathname;

      // 表示が許可された画面か
      const isAllowed = PATHNAME_STATUS_MAP[generalizedPath]?.includes(status);

      if (!isAllowed) {
        const redirectUrl = getRedirectUrlByStatus(status);
        navigate(redirectUrl);
      }
    })();
  }, [location.pathname, repairRequest?.rmaNo]);

  return (
    <Routes>
      <Route path={INSPEX_PATHNAME.LOGIN} element={<Login />} />
      <Route
        path={INSPEX_PATHNAME.RMA_SEARCH}
        element={<RepairRequestSearch />}
      />
      <Route path={INSPEX_PATHNAME.SERIAL_CHECK} element={<SerialCheck />} />
      <Route
        path={INSPEX_PATHNAME.INSPECTION_TOP}
        element={<InspectionTop />}
      />
      <Route
        path={`${INSPEX_PATHNAME.INSPECTION}:id`}
        element={<Inspection />}
      />
      <Route
        path={INSPEX_PATHNAME.INSPECTION_INPUT_RESULT}
        element={<InputResult />}
      />
      <Route
        path={INSPEX_PATHNAME.CONFIRMED_INSPECTION}
        element={<ConfirmedInspection />}
      />
      <Route
        path={INSPEX_PATHNAME.PARTS_SELECTION}
        element={<PartSelection />}
      />
      <Route path={INSPEX_PATHNAME.REPORT_FORMAT} element={<ReportFormat />} />
      <Route path={INSPEX_PATHNAME.REPORT} element={<Report />} />
      <Route
        path={INSPEX_PATHNAME.MANAGE_INSPECTION_ITEM}
        element={<ManageInspectionItem />}
      />
      <Route
        path={INSPEX_PATHNAME.MANAGE_CRITERIA}
        element={<ManageCriteria />}
      />
      <Route
        path={INSPEX_PATHNAME.MANAGE_TEMPLATE}
        element={<ManageTemplate />}
      />
      <Route path={INSPEX_PATHNAME.MANAGE_USER} element={<ManageUser />} />
      <Route
        path={INSPEX_PATHNAME.MANAGE_ORGANIZATION}
        element={<ManageOrganization />}
      />
      <Route
        path={INSPEX_PATHNAME.MANAGE_CODE_MASTER}
        element={<ManageCodeMaster />}
      />
      <Route
        path={INSPEX_PATHNAME.MANAGE_HISTORY}
        element={<ManageHistory />}
      />
      <Route path={INSPEX_PATHNAME.CONFIRMED_QC} element={<ConfirmedQc />} />
      {/* TODO : 動作確認後に削除 */}
      <Route path="/ExFM" element={<ExFM />} />
    </Routes>
  );
};

export default App;
